<template>
    <g-base-box class="margin-bottom-large" padding="0">
        <div class="task-header g-p-5">
            <g-media-object vertical-align="center">
                <template #left>
                    <g-typography font-weight="semibold" :value="brief.activation.public_name" />
                </template>
                <template #right>
                    <g-typography size="small">
                        <a @click="handleReviewRequirements">Review requirements</a>
                    </g-typography>
                </template>
            </g-media-object>
        </div>

        <div class="task-body">
            <div v-for="task in brief.tasks" :key="task.id" class="task-item g-p-5">
                <g-media-object>
                    <template #left>
                        <activation-task-icon :task="task" />
                    </template>
                    <template #default>
                        <g-typography tag="p" :value="taskName(task)" class="g-mb-2" />

                        <g-typography color="grey-6" size="small">
                            <span v-if="isTaskComplete(task)">
                                <g-icon name="check-circle" color="green-5" type="solid" size="small" />
                                Complete
                            </span>

                            <span v-else>
                                {{ taskRemaining(task) }}
                            </span>
                        </g-typography>
                    </template>
                    <template #right>
                        <g-button
                            shape="round"
                            theme="live"
                            :type="isTaskComplete(task) ? 'default' : 'primary'"
                            @click="handleDeliver(task)"
                        >
                            Deliver
                        </g-button>
                    </template>
                </g-media-object>
            </div>
        </div>
    </g-base-box>
</template>

<script>
    import { toRefs } from 'vue-demi';
    import {
        ACTIVATION_TASK_STATUS_COMPLETE,
        ACTIVATION_TASK_STATUS_EVERGREEN,
        ACTIVATION_TASK_STATUS_LABELS
    } from '../../../../constants/activation-task';
    import {
        EVENT_NAME_TASKS_SUMMARY_REVIEW_REQUIREMENTS_CLICKED,
        EVENT_NAME_TASKS_SUMMARY_DELIVER_CLICKED
    } from '../../../../constants/analytics-events';
    import ActivationTaskIcon from '../partials/tasks/ActivationTaskIcon.vue';
    import useTaskDelivery from '../composables/use-task-delivery';
    import useEventTracking from '../../../../../src/composables/use-event-tracking';
    import { useContactStore } from '../../../../../stores';
    import useRouter from '../../../../../src/composables/use-router';

    export default {
        name: 'ActivationCard',
        components: {
            ActivationTaskIcon
        },
        props: {
            brief: {
                type: Object,
                required: true
            }
        },

        setup(props) {
            const contact = useContactStore().data;
            const { goToDeliveryPage } = useTaskDelivery();
            const { brief } = toRefs(props);
            const router = useRouter().get();

            const isTaskComplete = (task) => {
                return task?.status === ACTIVATION_TASK_STATUS_COMPLETE ? true : false;
            };

            const taskName = (task) => {
                if (!task) {
                    return '';
                }

                return `${task.name} ${task.complete_quantity}/${task.quantity !== 0 ? task.quantity : '∞'}`;
            };

            const taskRemaining = (task) => {
                const remaining = task.quantity - task.complete_quantity;

                return task.status === ACTIVATION_TASK_STATUS_EVERGREEN
                    ? ACTIVATION_TASK_STATUS_LABELS[ACTIVATION_TASK_STATUS_EVERGREEN]
                    : `${remaining} deliverable${remaining > 1 ? 's' : ''} remaining`;
            };

            const handleReviewRequirements = () => {
                useEventTracking().trackEvent(EVENT_NAME_TASKS_SUMMARY_REVIEW_REQUIREMENTS_CLICKED, {
                    contact_id: contact?.id
                });

                router.push({
                    name: 'v2.activation.brief.tasks',
                    params: {
                        brief_id: brief.value.id,
                        context: 'summary',
                        back_route: 'v2.activation.tasks.summary'
                    }
                });
            };

            const handleDeliver = (task) => {
                useEventTracking().trackEvent(EVENT_NAME_TASKS_SUMMARY_DELIVER_CLICKED, {
                    contact_id: contact?.id
                });

                goToDeliveryPage(task, false, null, {
                    context: 'summary',
                    back_route: 'v2.activation.tasks.summary'
                });
            };

            return {
                isTaskComplete,
                taskName,
                taskRemaining,
                handleReviewRequirements,
                handleDeliver
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .task-header {
        background-color: $g-color-grey-2;
    }

    .task-header,
    .task-item {
        border-bottom: 1px solid $g-color-grey-4;
    }

    .task-item {
        &:last-child {
            border-bottom: 0 none;
        }
    }
</style>
